import { useCallback, useEffect, useState } from 'react';

import {
  Alert,
  Button,
  Card,
  CheckIcon,
  Popover,
  PopoverButton,
  PopoverClose,
  PopoverContent,
  PopoverTrigger,
  cn,
} from '@playerbase/ui';
import {
  StudioMemberSummarySchema,
  type StudioMemberSummary,
  StudioInviteSchema,
  type StudioInvite,
  type EditableStudioRole,
  type StudioRole,
} from '@playerbase/types';

import { supabase } from '../../lib/supabase.js';
import { useAuth } from '../../auth/use-auth.js';
import { useShell } from '../../shell/use-shell.js';
import { InviteModal } from './invite-modal.js';

type RowState = 'idle' | 'busy';

type PendingChange = {
  role?: EditableStudioRole;
  revoked?: boolean;
};

const ROLE_OPTIONS: ReadonlyArray<{ value: EditableStudioRole; label: string }> = [
  { value: 'owner', label: 'Owner' },
  { value: 'member', label: 'Member' },
];

function formatDate(iso: string | null): string {
  if (iso === null) return '-';
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

function formatRole(role: StudioRole): string {
  return role.charAt(0).toUpperCase() + role.slice(1);
}

function mapInviteRow(raw: Record<string, unknown>): StudioInvite | null {
  const result = StudioInviteSchema.safeParse({
    id: raw.id,
    studioId: raw.studio_id,
    email: raw.email,
    role: raw.role,
    personalMessage: raw.personal_message,
    invitedByUserId: raw.invited_by_user_id,
    expiresAt: raw.expires_at,
    sentAt: raw.sent_at,
    lastSentAt: raw.last_sent_at,
    acceptedAt: raw.accepted_at,
    acceptedByUserId: raw.accepted_by_user_id,
    revokedAt: raw.revoked_at,
    revokedByUserId: raw.revoked_by_user_id,
    createdAt: raw.created_at,
    updatedAt: raw.updated_at,
  });
  if (!result.success) {
    console.warn('[MembersPanel] could not parse invite row:', result.error.issues);
    return null;
  }
  return result.data;
}

export function MembersPanel(): JSX.Element {
  const { activeStudio } = useShell();
  const { user, memberships } = useAuth();
  const studioId = activeStudio.id;

  const currentMembership = memberships.find((m) => m.studioId === studioId);
  const isOwner = currentMembership?.role === 'owner';

  const [members, setMembers] = useState<StudioMemberSummary[]>([]);
  const [pending, setPending] = useState<StudioInvite[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [rowStates, setRowStates] = useState<Record<string, RowState>>({});
  const [rowError, setRowError] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [pendingChanges, setPendingChanges] = useState<Record<string, PendingChange>>({});
  const [saving, setSaving] = useState(false);

  const loadAll = useCallback(async (): Promise<void> => {
    setLoadError(null);
    const [membersRes, invitesRes] = await Promise.all([
      supabase.rpc('list_studio_members', { p_studio_id: studioId }),
      supabase
        .from('studio_invites')
        .select('*')
        .eq('studio_id', studioId)
        .is('accepted_at', null)
        .is('revoked_at', null)
        .order('created_at', { ascending: false }),
    ]);

    if (membersRes.error !== null) {
      setLoadError(membersRes.error.message);
      setMembers([]);
    } else {
      const parsed: StudioMemberSummary[] = [];
      for (const raw of (membersRes.data ?? []) as unknown[]) {
        const r = StudioMemberSummarySchema.safeParse(raw);
        if (r.success) parsed.push(r.data);
        else console.warn('[MembersPanel] could not parse member row:', r.error.issues);
      }
      setMembers(parsed);
    }

    if (invitesRes.error !== null) {
      setLoadError(invitesRes.error.message);
      setPending([]);
    } else {
      const parsed: StudioInvite[] = [];
      for (const raw of (invitesRes.data ?? []) as Record<string, unknown>[]) {
        const r = mapInviteRow(raw);
        if (r !== null) parsed.push(r);
      }
      setPending(parsed);
    }

    setLoading(false);
  }, [studioId]);

  useEffect(() => {
    setLoading(true);
    void loadAll();
  }, [loadAll]);

  // Reset edit state when the active studio changes (sidebar switcher).
  useEffect(() => {
    setEditMode(false);
    setPendingChanges({});
    setRowError(null);
  }, [studioId]);

  async function handleResend(inviteId: string): Promise<void> {
    setRowError(null);
    setRowStates((s) => ({ ...s, [inviteId]: 'busy' }));
    try {
      const { error } = await supabase.functions.invoke('studio-invites', {
        body: { action: 'resend', inviteId },
      });
      if (error !== null) {
        setRowError(`Could not resend invite: ${error.message}`);
        return;
      }
      await loadAll();
    } finally {
      setRowStates((s) => ({ ...s, [inviteId]: 'idle' }));
    }
  }

  async function handleRevokeInvite(inviteId: string): Promise<void> {
    if (!window.confirm('Revoke this invite? The recipient will see a "revoked" state if they click the link.')) return;
    setRowError(null);
    setRowStates((s) => ({ ...s, [inviteId]: 'busy' }));
    try {
      const { error } = await supabase.rpc('revoke_studio_invite', { p_invite_id: inviteId });
      if (error !== null) {
        setRowError(`Could not revoke invite: ${error.message}`);
        return;
      }
      await loadAll();
    } finally {
      setRowStates((s) => ({ ...s, [inviteId]: 'idle' }));
    }
  }

  function effectiveRole(member: StudioMemberSummary): StudioRole {
    return pendingChanges[member.memberId]?.role ?? member.role;
  }

  function isStagedRevoke(member: StudioMemberSummary): boolean {
    return pendingChanges[member.memberId]?.revoked === true;
  }

  function setStagedRole(
    memberId: string,
    role: EditableStudioRole,
    originalRole: StudioRole,
  ): void {
    setPendingChanges((prev) => {
      const next = { ...prev };
      const existing = next[memberId] ?? {};
      if (role === originalRole) {
        const { role: _r, ...rest } = existing;
        if (Object.keys(rest).length === 0) delete next[memberId];
        else next[memberId] = rest;
      } else {
        next[memberId] = { ...existing, role };
      }
      return next;
    });
  }

  function toggleStagedRevoke(memberId: string): void {
    setPendingChanges((prev) => {
      const next = { ...prev };
      const existing = next[memberId] ?? {};
      if (existing.revoked === true) {
        const { revoked: _r, ...rest } = existing;
        if (Object.keys(rest).length === 0) delete next[memberId];
        else next[memberId] = rest;
      } else {
        next[memberId] = { ...existing, revoked: true };
      }
      return next;
    });
  }

  function exitEditMode(): void {
    setEditMode(false);
    setPendingChanges({});
    setRowError(null);
  }

  const stagedCount = Object.keys(pendingChanges).length;

  async function handleSaveChanges(): Promise<void> {
    setRowError(null);
    setSaving(true);
    try {
      const entries = Object.entries(pendingChanges);
      // Apply promotions to owner first so subsequent demotions/revokes don't
      // trip the last-owner guard when the user is rotating who is in charge.
      const promotions = entries.filter(([, c]) => !c.revoked && c.role === 'owner');
      const demotions = entries.filter(([, c]) => !c.revoked && c.role !== undefined && c.role !== 'owner');
      const revokes = entries.filter(([, c]) => c.revoked === true);

      for (const [memberId, change] of [...promotions, ...demotions]) {
        if (change.role === undefined) continue;
        const { error } = await supabase.rpc('update_studio_member_role', {
          p_member_id: memberId,
          p_role: change.role,
        });
        if (error !== null) {
          setRowError(`Could not save changes: ${error.message}`);
          await loadAll();
          return;
        }
      }
      for (const [memberId] of revokes) {
        const { error } = await supabase.rpc('revoke_studio_member', {
          p_member_id: memberId,
        });
        if (error !== null) {
          setRowError(`Could not save changes: ${error.message}`);
          await loadAll();
          return;
        }
      }

      setPendingChanges({});
      setEditMode(false);
      await loadAll();
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="mx-auto flex max-w-3xl flex-col gap-6 px-4 py-6 sm:px-6 sm:py-10">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="font-display text-xl font-bold text-text">Members</h2>
          <p className="mt-1 text-sm text-text-muted">
            People who can access {activeStudio.name}.
          </p>
        </div>
        {editMode ? (
          <div className="flex gap-2">
            <Button variant="ghost" disabled={saving} onClick={exitEditMode}>
              Cancel
            </Button>
            <Button
              variant="primary"
              disabled={saving || stagedCount === 0}
              onClick={() => { void handleSaveChanges(); }}
            >
              {saving ? 'Saving…' : 'Save changes'}
            </Button>
          </div>
        ) : (
          <div className="flex gap-2">
            <Button variant="primary" onClick={() => setModalOpen(true)}>
              + Invite member
            </Button>
            {isOwner && (
              <Button variant="ghost" onClick={() => setEditMode(true)}>
                Edit members
              </Button>
            )}
          </div>
        )}
      </div>

      {loadError !== null && <Alert tone="danger">{loadError}</Alert>}
      {rowError !== null && <Alert tone="danger">{rowError}</Alert>}

      <Card>
        <Card.Header>
          <Card.Title>Current members</Card.Title>
        </Card.Header>
        <Card.Content>
          {loading ? (
            <p className="text-sm text-text-muted">Loading&hellip;</p>
          ) : members.length === 0 ? (
            <p className="text-sm text-text-muted">No members yet.</p>
          ) : (
            <ul className="divide-y divide-border">
              {members.map((m) => {
                const isMe = m.userId === user?.id;
                if (!editMode) {
                  return (
                    <li key={m.memberId} className="flex items-center justify-between gap-4 py-3">
                      <div className="min-w-0">
                        <p className="truncate text-sm font-medium text-text">{m.displayName}</p>
                        <p className="truncate text-xs text-text-muted">{m.email ?? '-'}</p>
                      </div>
                      <div className="text-right text-xs text-text-muted">
                        <p className="capitalize">{m.role}</p>
                        <p>Joined {formatDate(m.joinedAt)}</p>
                      </div>
                    </li>
                  );
                }
                const revoked = isStagedRevoke(m);
                const role = effectiveRole(m);
                const stagedRoleChange =
                  pendingChanges[m.memberId]?.role !== undefined &&
                  pendingChanges[m.memberId]?.role !== m.role;
                return (
                  <li
                    key={m.memberId}
                    className={cn(
                      'flex items-center justify-between gap-4 py-3',
                      revoked && 'opacity-60',
                    )}
                  >
                    <div className="min-w-0 flex-1">
                      <p className={cn('truncate text-sm font-medium text-text', revoked && 'line-through')}>
                        {m.displayName}
                        {isMe && <span className="ml-1 text-text-muted">(you)</span>}
                      </p>
                      <p className={cn('truncate text-xs text-text-muted', revoked && 'line-through')}>
                        {m.email ?? '-'}
                      </p>
                    </div>
                    {isMe ? (
                      <p className="text-xs text-text-muted">{formatRole(m.role)}</p>
                    ) : (
                      <div className="flex items-center gap-2">
                        {(stagedRoleChange || revoked) && (
                          <span className="text-xs text-text-muted">Unsaved</span>
                        )}
                        <Popover>
                          <PopoverTrigger asChild>
                            <PopoverButton
                              size="sm"
                              disabled={revoked || saving}
                              className="min-w-[7rem]"
                            >
                              <span className="flex-1 text-left">{formatRole(role)}</span>
                            </PopoverButton>
                          </PopoverTrigger>
                          <PopoverContent align="end" className="w-[10rem]">
                            {ROLE_OPTIONS.map(({ value, label }) => (
                              <PopoverClose key={value} asChild>
                                <button
                                  type="button"
                                  className="flex w-full items-center gap-2 rounded-sm px-3 py-2 text-left text-sm text-text hover:bg-surface-2"
                                  onClick={() => setStagedRole(m.memberId, value, m.role)}
                                >
                                  <span className="flex-1">{label}</span>
                                  {role === value && <CheckIcon className="text-accent" />}
                                </button>
                              </PopoverClose>
                            ))}
                          </PopoverContent>
                        </Popover>
                        <Button
                          variant="ghost"
                          size="sm"
                          disabled={saving}
                          onClick={() => toggleStagedRevoke(m.memberId)}
                        >
                          {revoked ? 'Undo' : 'Revoke'}
                        </Button>
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </Card.Content>
      </Card>

      <Card>
        <Card.Header>
          <Card.Title>Pending invites</Card.Title>
        </Card.Header>
        <Card.Content>
          {loading ? (
            <p className="text-sm text-text-muted">Loading&hellip;</p>
          ) : pending.length === 0 ? (
            <p className="text-sm text-text-muted">No pending invites.</p>
          ) : (
            <ul className="divide-y divide-border">
              {pending.map((invite) => {
                const busy = rowStates[invite.id] === 'busy';
                return (
                  <li key={invite.id} className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
                    <div className="min-w-0">
                      <p className="truncate text-sm font-medium text-text">{invite.email}</p>
                      <p className="text-xs text-text-muted">
                        Sent {formatDate(invite.sentAt)} &middot; expires {formatDate(invite.expiresAt)}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <Button variant="ghost" disabled={busy} onClick={() => { void handleResend(invite.id); }}>
                        Resend
                      </Button>
                      <Button variant="ghost" disabled={busy} onClick={() => { void handleRevokeInvite(invite.id); }}>
                        Revoke
                      </Button>
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </Card.Content>
      </Card>

      {modalOpen && (
        <InviteModal
          studioId={studioId}
          studioName={activeStudio.name}
          onClose={() => setModalOpen(false)}
          onSent={() => { void loadAll(); }}
        />
      )}
    </div>
  );
}
